export type BidSnapshotStatus = 'capturing' | 'ready' | 'partial' | 'failed';

export type BidSnapshotApplyJobStatus =
  | 'pending'
  | 'running'
  | 'completed'
  | 'completed_with_errors'
  | 'failed';

export type BidSnapshotApplyResultStatus = 'pending' | 'applied' | 'skipped' | 'failed';

export type BidSnapshotEntityCounts = {
  sp_keywords?: number;
  sb_keywords?: number;
  sp_targets?: number;
  sb_targets?: number;
  total_saved?: number;
  total_failed_capture?: number;
};

export type BidSnapshot = {
  id: string;
  profile_id: string;
  name: string;
  note: string | null;
  status: BidSnapshotStatus;
  entity_counts: BidSnapshotEntityCounts;
  capture_progress: Record<string, unknown>;
  capture_error: string | null;
  catalog_as_of: string | null;
  catalog_stale: boolean;
  capture_source: string;
  created_by_user_id: string | null;
  created_at: string | null;
  updated_at: string | null;
};

export type BidSnapshotItem = {
  id: string;
  entity_type: string;
  entity_id: string;
  campaign_id: string | null;
  ad_group_id: string | null;
  entity_label: string | null;
  bid: number;
  state: string | null;
};

export type BidSnapshotApplyJob = {
  id: string;
  snapshot_id: string;
  profile_id: string;
  status: BidSnapshotApplyJobStatus;
  totals: Record<string, number>;
  apply_progress: Record<string, unknown>;
  started_at: string | null;
  finished_at: string | null;
  created_at: string | null;
};

export type BidSnapshotApplyResult = {
  id: string;
  apply_job_id: string;
  snapshot_item_id: string;
  entity_type: string;
  entity_id: string;
  entity_label: string | null;
  snapshot_bid: number;
  previous_bid: number | null;
  status: BidSnapshotApplyResultStatus;
  error_code: string | null;
  error_message: string | null;
  applied_at: string | null;
  retry_count: number;
};

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

export function parseBidSnapshot(raw: unknown): BidSnapshot | null {
  const o = asRecord(raw);
  const id = String(o.id ?? '').trim();
  if (!id) return null;
  return {
    id,
    profile_id: String(o.profile_id ?? ''),
    name: String(o.name ?? ''),
    note: o.note != null ? String(o.note) : null,
    status: (String(o.status ?? 'capturing') as BidSnapshotStatus),
    entity_counts: asRecord(o.entity_counts) as BidSnapshotEntityCounts,
    capture_progress: asRecord(o.capture_progress),
    capture_error: o.capture_error != null ? String(o.capture_error) : null,
    catalog_as_of: o.catalog_as_of != null ? String(o.catalog_as_of) : null,
    catalog_stale: Boolean(o.catalog_stale),
    capture_source: String(o.capture_source ?? 'entity_catalog'),
    created_by_user_id: o.created_by_user_id != null ? String(o.created_by_user_id) : null,
    created_at: o.created_at != null ? String(o.created_at) : null,
    updated_at: o.updated_at != null ? String(o.updated_at) : null,
  };
}

export function parseBidSnapshotList(data: unknown): BidSnapshot[] {
  const root = asRecord(data);
  const list = root.snapshots;
  if (!Array.isArray(list)) return [];
  return list.map(parseBidSnapshot).filter((x): x is BidSnapshot => x != null);
}

export function parseBidSnapshotItem(raw: unknown): BidSnapshotItem | null {
  const o = asRecord(raw);
  const id = String(o.id ?? '').trim();
  if (!id) return null;
  return {
    id,
    entity_type: String(o.entity_type ?? ''),
    entity_id: String(o.entity_id ?? ''),
    campaign_id: o.campaign_id != null ? String(o.campaign_id) : null,
    ad_group_id: o.ad_group_id != null ? String(o.ad_group_id) : null,
    entity_label: o.entity_label != null ? String(o.entity_label) : null,
    bid: Number(o.bid ?? 0),
    state: o.state != null ? String(o.state) : null,
  };
}

export function parseBidSnapshotApplyJob(raw: unknown): BidSnapshotApplyJob | null {
  const o = asRecord(raw);
  const id = String(o.id ?? '').trim();
  if (!id) return null;
  return {
    id,
    snapshot_id: String(o.snapshot_id ?? ''),
    profile_id: String(o.profile_id ?? ''),
    status: String(o.status ?? 'pending') as BidSnapshotApplyJobStatus,
    totals: asRecord(o.totals) as Record<string, number>,
    apply_progress: asRecord(o.apply_progress),
    started_at: o.started_at != null ? String(o.started_at) : null,
    finished_at: o.finished_at != null ? String(o.finished_at) : null,
    created_at: o.created_at != null ? String(o.created_at) : null,
  };
}

export function parseBidSnapshotApplyResult(raw: unknown): BidSnapshotApplyResult | null {
  const o = asRecord(raw);
  const id = String(o.id ?? '').trim();
  if (!id) return null;
  return {
    id,
    apply_job_id: String(o.apply_job_id ?? ''),
    snapshot_item_id: String(o.snapshot_item_id ?? ''),
    entity_type: String(o.entity_type ?? ''),
    entity_id: String(o.entity_id ?? ''),
    entity_label: o.entity_label != null ? String(o.entity_label) : null,
    snapshot_bid: Number(o.snapshot_bid ?? 0),
    previous_bid: o.previous_bid != null ? Number(o.previous_bid) : null,
    status: String(o.status ?? 'pending') as BidSnapshotApplyResultStatus,
    error_code: o.error_code != null ? String(o.error_code) : null,
    error_message: o.error_message != null ? String(o.error_message) : null,
    applied_at: o.applied_at != null ? String(o.applied_at) : null,
    retry_count: Number(o.retry_count ?? 0),
  };
}

export function entityTypeLabel(entityType: string): string {
  switch (entityType) {
    case 'sp_keyword':
      return 'SP keyword';
    case 'sb_keyword':
      return 'SB keyword';
    case 'sp_target':
      return 'SP target';
    case 'sb_target':
      return 'SB target';
    default:
      return entityType;
  }
}

export function snapshotEntityTotal(counts: BidSnapshotEntityCounts): number {
  return Number(counts.total_saved ?? 0);
}

export function formatSnapshotStatus(status: string): string {
  switch (status) {
    case 'capturing':
      return 'Capturing…';
    case 'ready':
      return 'Ready';
    case 'partial':
      return 'Partial';
    case 'failed':
      return 'Failed';
    case 'completed_with_errors':
      return 'Completed with errors';
    case 'running':
      return 'Running…';
    case 'completed':
      return 'Completed';
    default:
      return status;
  }
}
